import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { Link as RouterLink } from 'react-router-dom';
import { CoralResponse } from '../types';
import { getCoralChildren } from '../services/api';

interface CoralFamilyProps {
  coral: CoralResponse;
}

const CHILDREN_PAGE_SIZE = 25;

// A coral with no recorded parent is F2; every child is FN regardless of depth.
const generationLabel = (node: CoralResponse): string => (node.parentFragmentId ? 'FN' : 'F2');

const CoralFamily: React.FC<CoralFamilyProps> = ({ coral }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [children, setChildren] = useState<CoralResponse[]>([]);
  const [childrenCursor, setChildrenCursor] = useState<string | null | undefined>();
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [childrenLoaded, setChildrenLoaded] = useState(false);
  const [childrenError, setChildrenError] = useState<string | null>(null);

  const lineageNodes = useMemo(() => [...(coral.lineage || [])].reverse().concat(coral), [coral]);

  useEffect(() => {
    setActiveTab(0);
    setChildren([]);
    setChildrenCursor(undefined);
    setChildrenLoaded(false);
    setChildrenError(null);
  }, [coral.coralId]);

  const loadChildren = useCallback(
    async (cursor?: string, append = false) => {
      try {
        setChildrenLoading(true);
        setChildrenError(null);
        const response = await getCoralChildren(coral.coralId, CHILDREN_PAGE_SIZE, cursor);
        setChildren((current) => (append ? [...current, ...response.items] : response.items));
        setChildrenCursor(response.nextCursor);
        setChildrenLoaded(true);
      } catch (error: any) {
        setChildrenError(error?.message || 'Failed to load child fragments');
        setChildrenLoaded(true);
      } finally {
        setChildrenLoading(false);
      }
    },
    [coral.coralId]
  );

  useEffect(() => {
    if (activeTab === 1 && !childrenLoaded && !childrenLoading) {
      void loadChildren();
    }
  }, [activeTab, childrenLoaded, childrenLoading, loadChildren]);

  const handleLoadMore = useCallback(() => {
    if (childrenCursor && !childrenLoading) {
      void loadChildren(childrenCursor, true);
    }
  }, [childrenCursor, childrenLoading, loadChildren]);

  return (
    <Paper sx={{ mt: 3, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 2,
          pt: 2,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountTreeIcon color="primary" fontSize="small" />
            <Typography variant="h6">Fragment Lineage</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Follow this coral back through its parent coral IDs or view its direct children.
          </Typography>
        </Box>
        {coral.rfid ? (
          <Button
            component={RouterLink}
            to={`/coral/view/${encodeURIComponent(coral.coralId)}/fragments/create`}
            variant="contained"
            startIcon={<AddIcon />}
          >
            Create Fragments
          </Button>
        ) : (
          <Button variant="contained" disabled title="Only tagged corals can be parents">
            Create Fragments
          </Button>
        )}
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_event, value: number) => setActiveTab(value)}
        sx={{ px: 2, mt: 1 }}
        aria-label="Coral lineage and children"
      >
        <Tab label="Lineage" />
        <Tab label="Children" />
      </Tabs>
      <Divider />

      {activeTab === 0 && (
        <Box sx={{ p: 2 }}>
          {!coral.parentFragmentId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This coral has no recorded parent and is the known origin of this lineage.
            </Alert>
          )}
          <Box sx={{ overflowX: 'auto', pb: 1 }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ minWidth: 'max-content' }}
            >
              {lineageNodes.map((node, index) => {
                const isCurrent = node.coralId === coral.coralId;
                return (
                  <React.Fragment key={node.coralId}>
                    {index > 0 && <ArrowBackIcon color="action" fontSize="small" />}
                    <Button
                      component={isCurrent ? 'button' : RouterLink}
                      to={isCurrent ? undefined : `/coral/view/${encodeURIComponent(node.coralId)}`}
                      variant={isCurrent ? 'contained' : 'outlined'}
                      color={isCurrent ? 'primary' : 'inherit'}
                      disabled={isCurrent}
                      sx={{
                        minWidth: 190,
                        px: 2,
                        py: 1.5,
                        textTransform: 'none',
                        '&.Mui-disabled': {
                          color: 'primary.contrastText',
                          bgcolor: 'primary.main',
                          opacity: 1,
                        },
                      }}
                    >
                      <Stack spacing={0.5} alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {generationLabel(node)}
                          {isCurrent ? ' (Current)' : ''}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {node.rfid || 'Untagged coral'}
                        </Typography>
                        {node.parentFragmentId && (
                          <Typography variant="caption">Parent: {node.parentFragmentId}</Typography>
                        )}
                        <Typography variant="caption">{node.speciesCode}</Typography>
                        <Chip
                          label={node.status}
                          size="small"
                          color={node.status === 'ALIVE' ? 'success' : 'default'}
                          variant={isCurrent ? 'filled' : 'outlined'}
                        />
                      </Stack>
                    </Button>
                  </React.Fragment>
                );
              })}
            </Stack>
          </Box>
          {coral.lineage && coral.lineage.length === 4 && (
            <Typography variant="caption" color="text.secondary">
              Showing the four nearest recorded ancestors. Open the oldest visible coral to continue
              toward the origin.
            </Typography>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{ p: 2 }}>
          {childrenError && (
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Alert severity="error">{childrenError}</Alert>
              {children.length === 0 && (
                <Button
                  variant="outlined"
                  onClick={() => void loadChildren()}
                  disabled={childrenLoading}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Retry
                </Button>
              )}
            </Stack>
          )}
          {childrenLoading && children.length === 0 ? (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <CircularProgress size={24} />
              <Typography variant="body2">Loading child fragments...</Typography>
            </Stack>
          ) : childrenLoaded && children.length === 0 && !childrenError ? (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              This coral has no recorded child fragments.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {children.map((child) => (
                <Paper key={child.coralId} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {child.rfid || 'Untagged coral'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {child.speciesCode} · {child.status}
                        {child.cutDate ? ` · Cut ${child.cutDate}` : ''}
                      </Typography>
                    </Box>
                    <Button
                      component={RouterLink}
                      to={`/coral/view/${encodeURIComponent(child.coralId)}`}
                      size="small"
                    >
                      View Fragment
                    </Button>
                  </Stack>
                </Paper>
              ))}
              {childrenCursor && (
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={childrenLoading}
                  sx={{ alignSelf: 'center' }}
                >
                  {childrenLoading ? 'Loading...' : 'Load More Children'}
                </Button>
              )}
            </Stack>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default CoralFamily;
